import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {useSelector} from 'react-redux'
import {useProductReview} from '../../context/ProductReviewContext'
import {getFieldSource, getProductReviewFieldsForCountry} from '../ProductReviewTabs/productReviewDataMap'
import {ProductReviewField} from './ProductReviewField'
import {ProductReviewMultiLanguageSection} from './ProductReviewMultiLanguageSection'

/**
 * @typedef {Object} ProductReviewDataProps
 * @property {Array<typeof import('../ProductReviewTabs/productReviewDataMap').PRODUCT_REVIEW_BASE_DATA_SECTION>} [sections] Section configuration list.
 * @property {Object.<string, string>} [fieldErrors] Optional map of `source.key` to an inline error message shown under the field's editable control.
 * @property {import('react').ReactNode} [children] Optional extra content rendered below mapped fields.
 */

/**
 * Renders product review fields based on section configuration.
 * For sections with multiLanguage: true, delegates to ProductReviewMultiLanguageSection.
 * @param {ProductReviewDataProps} props
 */
export const ProductReviewData = ({sections = [], fieldErrors = {}, children}) => {
  const {productData, updateEditableField} = useProductReview()
  const countryCode = useSelector(selectCountryCode)

  return (
    <div className="flex flex-col gap-4 divide-y divide-gray-200">
      {sections.map(section => {
        if (section.multiLanguage) {
          return <ProductReviewMultiLanguageSection key={section.id} section={section} />
        }

        return (
          <div key={section.id} className="divide-y divide-[var(--secondary-800)]">
            {getProductReviewFieldsForCountry(section.fields, countryCode).map(field => {
              const fieldSource = getFieldSource(field, countryCode)
              const sourceData = productData?.[fieldSource] ?? {}
              const primaryValue = sourceData?.[field.key]
              const value = primaryValue?.length || !field.fallbackKey ? primaryValue : sourceData?.[field.fallbackKey]

              // Compute effective required: true if field.required is true OR if requiredWhen condition is met
              let isRequired = field.required
              if (field.requiredWhen) {
                const [source, key] = field.requiredWhen.field.split('.')
                const conditionFieldValue = productData?.[source]?.[key]
                isRequired = isRequired || conditionFieldValue === field.requiredWhen.value
              }

              // Compute effective editable: use editableWhen condition if present, otherwise fall back to field.editable
              let isEditable = field.editable
              if (field.editableWhen) {
                const [source, key] = field.editableWhen.field.split('.')
                const conditionFieldValue = productData?.[source]?.[key]
                isEditable = conditionFieldValue === field.editableWhen.value
              }

              return (
                <div key={`${section.id}-${field.key}`}>
                  <ProductReviewField
                    labelKey={field.key}
                    label={field.label}
                    editable={isEditable}
                    required={isRequired}
                    renderer={field.renderer}
                    value={value}
                    error={fieldErrors[`${fieldSource}.${field.key}`]}
                    onChange={nextValue => {
                      // Keep the display key in sync when the value is saved under a different key
                      if (field.saveKey && field.saveKey !== field.key) {
                        updateEditableField(fieldSource, field.key, nextValue)
                      }
                      updateEditableField(
                        fieldSource,
                        field.saveKey || field.key,
                        field.parseValue ? field.parseValue(nextValue) : nextValue
                      )
                    }}
                    dependentDate={field.dependentDate ? sourceData?.[field.dependentDate] : undefined}
                  />
                </div>
              )
            })}
          </div>
        )
      })}
      {children}
    </div>
  )
}
